import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { Select } from "components/select/";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import TextField from "components/form/textField/TextField";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import {
  PostTeeSheetNotification,
  TTeesheetNotificationType,
} from "api/rpc/2024-04/facilityAdmin/teesheet/notification";

interface IValidatedField<T> {
  value: T;
  isValid: boolean;
  isDirty: boolean;
}

interface ITeeSheetNotificationNewState {
  startingDate: Date;
  endingDate: Date;
  courseId: number;
  userType: IValidatedField<TTeesheetNotificationType | undefined>;
  subject: IValidatedField<string>;
  content: IValidatedField<string>;
}

interface INotificationUserTypeOption {
  label: string;
  value: TTeesheetNotificationType;
}

export default function TeeSheetNotificationNew() {
  const history = useHistory();
  const { t } = useTranslation();

  const NO_COURSE_ID = -1;

  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(store => store.facilityStore);

  const notificationUserTypeOptions: INotificationUserTypeOption[] = useMemo(() => {
    return [
      {
        label: t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.001"),
        value: "admin",
      },
      {
        label: t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.002"),
        value: "customer",
      },
    ];
  }, []);

  const [state, setState] = useState<ITeeSheetNotificationNewState>({
    startingDate: new Date(),
    endingDate: new Date(),
    courseId: NO_COURSE_ID,
    userType: { value: undefined, isValid: false, isDirty: false },
    subject: { value: "", isValid: false, isDirty: false },
    content: { value: "", isValid: false, isDirty: false },
  });

  useEffect(() => {
    if (facilityStore.facility) {
      setState(prev => ({
        ...prev,
        courseId: facilityStore.facility.courses?.[0]?.id,
      }));
    }
  }, [facilityStore.facility]);

  async function postNotification() {
    setState(prev => ({
      ...prev,
      userType: { ...prev.userType, isDirty: true },
      subject: { ...prev.subject, isDirty: true },
      content: { ...prev.content, isDirty: true },
    }));

    if (!state.userType.isValid || !state.subject.isValid || !state.content.isValid) {
      return;
    }

    const postNotificationRes = await PostTeeSheetNotification(
      {
        start_date: state.startingDate.toISOString().slice(0, 10),
        end_date: state.endingDate.toISOString().slice(0, 10),
        course_id: state.courseId === NO_COURSE_ID ? undefined : state.courseId,
        subject: state.subject.value,
        user_type: state.userType.value,
        type: undefined,
        content: state.content.value,
      },
      true,
    );

    if (postNotificationRes.status !== StatusCode.OK) {
      dispatch(showError(postNotificationRes.data));
      return;
    }

    history.push("/admin/settings/tee-sheet/notification");
  }

  const primaryAction = {
    content: t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.003"),
    action: postNotification,
  };

  return (
    <Page
      title={t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.004")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.005"),
          url: "/admin/settings/tee-sheet/notification",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <DatePickerInput
                label={t("Notification Date Range")} // TODO: Translation
                months={1}
                position="left"
                startingDate={state.startingDate}
                setStartingDate={(updatedStartingDate: Date) =>
                  setState(prev => ({ ...prev, startingDate: updatedStartingDate }))
                }
                endingDate={state.endingDate}
                setEndingDate={(updatedEndingDate: Date) =>
                  setState(prev => ({ ...prev, endingDate: updatedEndingDate }))
                }
              />
            </FormLayout.Group>

            {facilityStore.facility?.courses?.length > 1 && (
              <FormLayout.Group>
                <Select
                  label={t("Is this notification specific to a course?")}
                  onChange={(courseId: number) => {
                    setState(prev => ({ ...prev, courseId }));
                  }}
                  defaultValue={state.courseId}
                >
                  <Select.Option value={NO_COURSE_ID} name={undefined}>
                    &nbsp;
                  </Select.Option>
                  {facilityStore.facility?.courses.map(course => {
                    return (
                      <Select.Option key={course.id} value={course.id} name={course.full_name}>
                        {course.full_name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
            )}
            <FormLayout.Group>
              <Select
                label={t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.006")}
                onChange={value =>
                  setState(prev => ({ ...prev, userType: { ...prev.userType, value, isValid: true, isDirty: true } }))
                }
                defaultValue={state.userType.value}
                error={!state.userType.isValid && state.userType.isDirty}
              >
                {notificationUserTypeOptions.map((option, index: number) => {
                  return (
                    <Select.Option key={index} value={option.value} name={option.label}>
                      {option.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={state.subject.value}
                label={t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.007")}
                onChange={e =>
                  setState(prev => ({
                    ...prev,
                    subject: {
                      ...prev.subject,
                      value: e.target.value,
                      isValid: e.target.value !== "",
                      isDirty: true,
                    },
                  }))
                }
                error={!state.subject.isValid && state.subject.isDirty}
                placeholder={t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.008")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                value={state.content.value}
                label={t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.009")}
                onChange={e =>
                  setState(prev => ({
                    ...prev,
                    content: {
                      ...prev.content,
                      value: e.target.value,
                      isValid: e.target.value !== "",
                      isDirty: true,
                    },
                  }))
                }
                error={!state.content.isValid && state.content.isDirty}
                placeholder={t("Message Contents")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
